import { configService } from './configService';

/**
 * Simplified Request Manager for basic deduplication
 * Only handles duplicate prevention for GET requests without complex caching
 */
class SimpleRequestManager {
  private pendingRequests = new Map<string, Promise<any>>();

  /**
   * Generate a simple key for request deduplication
   */
  private generateKey(url: string, options: RequestInit): string {
    const method = options.method || 'GET';
    const body = options.body || '';
    return `${method}:${url}:${body}`;
  }

  /**
   * Execute request with deduplication for GET requests
   */
  async dedupedRequest(
    url: string, 
    options: RequestInit, 
    apiRequestFn: (url: string, options: RequestInit) => Promise<any>
  ): Promise<any> {
    const method = (options.method || 'GET').toUpperCase();
    
    // Only deduplicate GET requests
    if (method !== 'GET') {
      return apiRequestFn(url, options);
    }

    const key = this.generateKey(url, options);
    
    // If the same GET request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Make new request and store the promise
    const requestPromise = apiRequestFn(url, options);
    this.pendingRequests.set(key, requestPromise);

    try {
      return await requestPromise;
    } finally {
      // Clean up the pending request after completion
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Clear all pending requests (useful for logout scenarios)
   */
  clearPendingRequests() {
    this.pendingRequests.clear();
  }

  /**
   * Get current pending request count (useful for debugging)
   */
  getPendingRequestCount() {
    return this.pendingRequests.size;
  }
}

// Global request manager instance
const requestManager = new SimpleRequestManager();

/**
 * Makes an authenticated API request to the server
 * @param url - The API endpoint URL (relative to API_BASE_URL)
 * @param options - Fetch options
 * @returns The response data as JSON
 */
export const apiRequest = async (url: string, options: RequestInit = {}) => {
  try {
    // Get API base URL from centralized config service
    const baseUrl = configService.getApiBaseUrl();
    
    // Prepare full URL - if url already contains http, use it directly
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
    
    // Optimize headers to reduce preflight requests
    const headers: Record<string, string> = {};
    
    // Only add Content-Type for requests with body (POST, PUT, PATCH)
    const hasBody = options.body !== undefined;
    const method = (options.method || 'GET').toUpperCase();
    const needsContentType = hasBody && ['POST', 'PUT', 'PATCH'].includes(method);
    
    if (needsContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Merge with any custom headers
    Object.assign(headers, options.headers);

    // Make the request with credentials to include cookies
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include', // This ensures cookies are sent with requests
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = { message: await response.text() };
    }

    // Handle API error responses
    if (response.status >= 400) {
      // Handle authentication errors
      if (response.status === 401) {
        // Check if this is a password expired scenario
        if (data.passwordExpired) {
          // Return the data for password expired case instead of throwing
          return data;
        }
        
        // Check if this is a force logout scenario (both tokens expired)
        if (data.forceLogout) {
          // Clear localStorage data 
          localStorage.removeItem('user');
          localStorage.removeItem('selectedCompany');

          // Dispatch custom event for SPA navigation
          if (window.location.pathname !== '/login') {
            window.dispatchEvent(new CustomEvent('auth-expired', { detail: { redirectTo: '/' } }));
          }
          throw new Error('Session expired. Please login again.');
        } else {
          // For 401 errors, use the server message if available, otherwise use default
          const errorMessage = data.message || 'Authentication expired. Please login again.';
          
          // Only clear localStorage and redirect for generic auth failures
          if (!data.message || errorMessage.toLowerCase().includes('expired')) {
            localStorage.removeItem('user');
            localStorage.removeItem('selectedCompany');
            
            if (window.location.pathname !== '/login') {
              window.dispatchEvent(new CustomEvent('auth-expired', { detail: { redirectTo: '/' } }));
            }
          }
          
          throw new Error(errorMessage);
        }
      }
      
      // For all other 400+ errors, extract the server message
      const errorMessage = data.message || data.error || `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    // Log more detailed error information for debugging
    if (error instanceof Error) {
      console.error('API request failed:', {
        message: error.message,
        url: url,
        method: options.method || 'GET'
      });
    } else {
      console.error('API request failed with unknown error:', error);
    }
    throw error;
  }
};

/**
 * Get data from an API endpoint with optional request deduplication
 * @param url - Relative URL (e.g., /api/users) or absolute URL
 * @param options - Fetch options (can include custom deduplication settings)
 */
export const getData = (url: string, options: RequestInit & { enableDeduplication?: boolean } = {}) => {
  const { enableDeduplication = true, ...requestOptions } = options;
  const finalOptions = { 
    method: 'GET', 
    ...requestOptions 
  };

  if (enableDeduplication) {
    return requestManager.dedupedRequest(url, finalOptions, apiRequest);
  } else {
    return apiRequest(url, finalOptions);
  }
};

/**
 * Post data to an API endpoint
 * @param url - Relative URL (e.g., /api/users) or absolute URL
 */
export const postData = async (url: string, data: any, options: RequestInit = {}) => {
  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * Update data at an API endpoint
 * @param url - Relative URL (e.g., /api/users) or absolute URL
 */
export const putData = async (url: string, data: any, options: RequestInit = {}) => {
  return apiRequest(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * Delete data at an API endpoint
 * @param url - Relative URL (e.g., /api/users) or absolute URL
 */
export const deleteData = async (url: string, options: RequestInit = {}) => {
  return apiRequest(url, {
    method: 'DELETE',
    ...options
  });
};

/**
 * Make any API request with deduplication control
 * @param url - The API endpoint URL
 * @param options - Fetch options (can include enableDeduplication flag)
 */
export const apiRequestWithDeduplication = (url: string, options: RequestInit & { enableDeduplication?: boolean } = {}) => {
  const { enableDeduplication = true, ...requestOptions } = options;
  
  if (enableDeduplication) {
    return requestManager.dedupedRequest(url, requestOptions, apiRequest);
  } else {
    return apiRequest(url, requestOptions);
  }
};

// Export request manager utilities for advanced usage
export { requestManager };

/**
 * Utility to get fresh data bypassing deduplication
 * Useful when you need to ensure fresh data from the server
 */
export const getFreshData = (url: string, options: RequestInit = {}) => {
  return getData(url, { ...options, enableDeduplication: false });
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get current company ID from localStorage
 */
export const getCurrentCompanyId = (): number | null => {
  try {
    const company = JSON.parse(localStorage.getItem('selectedCompany') || '{}');
    return company.id || null;
  } catch {
    return null;
  }
};

// ==========================================
// PAGINATION-AWARE API METHODS
// ==========================================

/**
 * Get paginated users with search support
 */
export const getPaginatedUsers = async (page = 1, limit = 10, search = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    search: search
  });
  return getData(`/api/users?${params.toString()}`);
};

/**
 * Get paginated groups with search support
 */
export const getPaginatedGroups = async (page = 1, limit = 10, search = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    search: search
  });
  return getData(`/api/groups?${params.toString()}`);
};

/**
 * Get paginated user-company assignments with search support
 */
export const getPaginatedUserCompanies = async (page = 1, limit = 10, search = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    search: search
  });
  return getData(`/api/user-companies?${params.toString()}`);
};

// ==========================================
// CONSOLIDATED API METHODS
// ==========================================

/**
 * Consolidated dashboard data fetch - reduces multiple API calls
 * Fetches all dashboard-related data in a single optimized request
 */
export const getDashboardData = async (companyId?: number, isAdmin?: boolean) => {
  const params = new URLSearchParams();
  if (companyId) params.append('companyId', String(companyId));
  if (isAdmin !== undefined) params.append('isAdmin', String(isAdmin));
  
  return getData(`/api/dashboard/consolidated?${params.toString()}`);
};

/**
 * SSO authentication with Azure AD
 * @param idToken - The actual ID token from MSAL (JWT)
 * @param provider - Authentication provider (hardcoded as "aptean")
 */
export const ssoLogin = async (idToken: string, provider: string = 'aptean') => {
  return postData('/api/v2/auth/login', {
    idToken,  // Send the actual ID token instead of username
    provider
  });
};

/**
 * Consolidated log stats - combines server and SMS logs in one request
 */
export const getLogStats = async (companyId?: number, startDate?: Date, endDate?: Date) => {
  const params = new URLSearchParams();
  if (companyId) params.append('companyId', String(companyId));
  if (startDate) params.append('startDate', startDate.toISOString());
  if (endDate) params.append('endDate', endDate.toISOString());
  
  return getData(`/api/logs/stats?${params.toString()}`);
};

// ==========================================
// COMPANY-SPECIFIC API METHODS
// ==========================================

/**
 * Company endpoints - now require companyId parameter
 */
export const getCompanyDetails = async (companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return getData(`/api/companies/${id}`);
};

export const updateCompany = async (companyData: any, companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return putData(`/api/companies/${id}`, companyData);
};

/**
 * Email configuration endpoints
 */
export const getEmailConfig = async (companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return getData(`/api/email/config/${id}`);
};

export const updateEmailConfig = async (config: any, companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return postData(`/api/email/config/${id}`, config);
};

/**
 * Update password/secret for a company (secure endpoint)
 */
export const updateCompanyPassword = async (password: string, authType: 'smtp' | 'oauth', companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return putData(`/api/companies/${id}/password`, {
    password,
    authType
  });
};

/**
 * Statistics endpoints
 */
export const getCustomerEmailStats = async (companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  return getData(`/api/email/stats/customer/${id}`);
};

/**
 * User companies - get all companies accessible to current user
 */
export const getUserCompanies = async () => {
  return getData('/api/companies');
};

/**
 * Email error logs with company parameter
 */
export const getEmailErrorLogs = async (page = 1, pageSize = 50, companyId?: number) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  const params = new URLSearchParams({
    companyId: String(id),
    page: String(page),
    pageSize: String(pageSize)
  });
  return getData(`/api/logs/email-errors?${params.toString()}`);
};

/**
 * SMS logs with company parameter - now using company-scoped endpoint
 */
export const getSmsLogs = async (page = 1, pageSize = 50, companyId?: number, startDate?: Date, endDate?: Date) => {
  const id = companyId || getCurrentCompanyId();
  if (!id) throw new Error('No company selected');
  
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize)
  });
  
  // Add date parameters if provided
  if (startDate) {
    params.append('startDate', startDate.toISOString().split('T')[0]); // Format as YYYY-MM-DD
  }
  
  if (endDate) {
    params.append('endDate', endDate.toISOString().split('T')[0]); // Format as YYYY-MM-DD
  }
  
  return getData(`/api/sms/logs/${id}?${params.toString()}`);
};

/**
 * Two-Factor Authentication API functions
 */
export const getTwoFactorStatus = async () => {
  return getData('/api/2fa/status');
};

export const setupTwoFactor = async () => {
  return postData('/api/2fa/setup', {});
};

export const enableTwoFactor = async (secret: string, token: string) => {
  return postData('/api/2fa/enable', { secret, token });
};

export const disableTwoFactor = async (currentPassword: string, isDeviceChange?: boolean) => {
  return postData('/api/2fa/disable', { 
    currentPassword,
    isDeviceChange: isDeviceChange || false 
  });
};

export const regenerateBackupCodes = async (token: string) => {
  return postData('/api/2fa/regenerate-codes', { token });
};

/**
 * Change user password
 */
export const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
  return putData('/api/users/change-password', {
    currentPassword,
    newPassword,
    confirmPassword
  });
};

// ==========================================
// SMTP POOL MANAGEMENT API METHODS
// ==========================================

/**
 * Get SMTP connection pool statistics
 */
export const getSMTPPoolStats = async () => {
  return getData('/api/smtp-pools/stats');
};

/**
 * Clear SMTP connection pool for a specific company
 */
export const clearCompanySMTPPool = async (companyCode: string) => {
  return deleteData(`/api/smtp-pools/company/${companyCode}`);
};

/**
 * Get SMTP pool health check
 */
export const getSMTPPoolHealth = async () => {
  return getData('/api/smtp-pools/health');
};

// ==========================================
// COMPANY TOKEN MANAGEMENT API METHODS
// ==========================================

/**
 * Generate new company token
 */
export const generateCompanyToken = async (companyId: number, tokenData: any) => {
  return postData(`/api/v2/companies/${companyId}/tokens`, tokenData);
};

/**
 * Get active tokens for a company
 */
export const getCompanyTokens = async (companyId: number) => {
  return getData(`/api/v2/companies/${companyId}/tokens`);
};

/**
 * Revoke specific company token
 */
export const revokeCompanyToken = async (companyId: number, tokenId: string) => {
  return deleteData(`/api/v2/companies/${companyId}/tokens/${tokenId}`);
};

/**
 * Revoke all company tokens
 */
export const revokeAllCompanyTokens = async (companyId: number) => {
  return deleteData(`/api/v2/companies/${companyId}/tokens`);
};

/**
 * Get all active tokens across all companies (Admin only)
 */
export const getAllActiveTokens = async () => {
  return getData('/api/v2/admin/company-tokens');
};

/**
 * Get company token statistics (Admin only)
 */
export const getTokenStatistics = async () => {
  return getData('/api/v2/admin/company-token-stats');
};

/**
 * Clean up expired tokens (Admin only)
 */
export const cleanupExpiredTokens = async () => {
  return postData('/api/v2/admin/company-tokens/cleanup', {});
};

// ==========================================
// ADMIN SESSION MANAGEMENT API METHODS
// ==========================================

/**
 * Get session statistics (Admin only)
 */
export const getSessionStats = async () => {
  return getData('/api/admin/sessions/stats');
};

/**
 * Get all active sessions (Admin only)
 */
export const getAllActiveSessions = async () => {
  return getData('/api/admin/sessions/all');
};

/**
 * Clean up expired sessions (Admin only)
 */
export const cleanupSessions = async () => {
  return postData('/api/admin/sessions/cleanup', {});
};

/**
 * Get sessions for a specific user (Admin only)
 */
export const getUserSessions = async (userId: number) => {
  return getData(`/api/admin/sessions/user/${userId}`);
};

/**
 * Revoke all sessions for a specific user (Admin only)
 */
export const revokeAllUserSessions = async (userId: number) => {
  return postData(`/api/admin/sessions/user/${userId}/revoke-all`, {});
};

/**
 * Revoke a specific token/session (Admin only)
 */
export const revokeSpecificToken = async (tokenId: string) => {
  return postData(`/api/admin/sessions/revoke/${tokenId}`, {});
};

/**
 * Get security events (Admin only)
 */
export const getSecurityEvents = async () => {
  return getData('/api/admin/sessions/security-events');
};

/**
 * Get security statistics (Admin only)
 */
export const getSecurityStats = async () => {
  return getData('/api/admin/sessions/security-stats');
};